import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { wsClient } from '../services/websocket';
import { sendCallHeartbeat } from '../services/callHistorySync';
import { useCallStore } from '../store/callStore';
import { useVaultStore } from '../store/vaultStore';
import { t } from '../i18n';
import { showToast } from '../utils/toast';
import { consumePendingPostCallJoin } from '../services/callLinkFlow';
import { createCallOrchestrator } from '../call/CallOrchestrator';
import { createCallEndHandler } from '../call/callLifecycle';
import { getActiveCallDurationSeconds, markCallEndedInHistory } from '../call/callHistoryEffects';
import { createIncomingCallCoordination } from '../call/callIncomingCoordination';
import { subscribeToCallNativeEvents } from '../call/callNativeEventHandlers';
import { consumeNativeCallMinimizedLaunchPending, getActiveNativeCalls, isWebViewMode } from '../utils/webviewBridge';
import { createPendingActionHandlers } from '../call/callPendingActionEffects';
import {
  closeWindowIncomingCallNotification,
  isLocalIncomingCallLeader,
  createIncomingCallUiEffects
} from '../call/callListenerEffects';
import { preflightWebVideoMediaAccess } from '../call/callMediaPreflight';
import { clearPendingCallAction, readPendingCallAction } from '../call/callPendingActions';
import { createCallPeerHandlers } from '../call/callPeerHandlers';
import { shouldIgnoreCallEndedSession } from '../call/callSessionEndGuards';
import { playOutgoingDialTone, playRingtone, stopOutgoingDialTone, stopRingtone, tryUnlockCallSounds } from '../call/callSoundEffects';
import { toggleVideo, toggleAudio } from '../call/callToggleActions';
import { createCallWsHandlers } from '../call/callWsHandlers';

const INCOMING_CALL_STALE_TIMEOUT_MS = (() => {
  const raw = Number((import.meta as any).env?.VITE_INCOMING_CALL_STALE_TIMEOUT_MS || 70_000);
  if (!Number.isFinite(raw) || raw < 10_000) return 70_000;
  return Math.floor(raw);
})();

const CALL_HISTORY_HEARTBEAT_INTERVAL_MS = (() => {
  const raw = Number((import.meta as any).env?.VITE_CALL_HISTORY_HEARTBEAT_MS || 15_000);
  if (!Number.isFinite(raw) || raw < 5_000) return 15_000;
  return Math.floor(raw);
})();

const NATIVE_CALL_RECONCILE_INTERVAL_MS = (() => {
  const raw = Number((import.meta as any).env?.VITE_NATIVE_CALL_RECONCILE_MS || 8_000);
  if (!Number.isFinite(raw) || raw < 3_000) return 8_000;
  return Math.floor(raw);
})();

function postToServiceWorker(message: any) {
  try {
    if (!('serviceWorker' in navigator)) return;
    const controller = navigator.serviceWorker.controller;
    if (!controller) return;
    controller.postMessage(message);
  } catch {
    // ignore
  }
}

function mapNativeCallStatus(state: string): 'connecting' | 'active' | 'handoff' {
  if (state === 'handoff') return 'handoff';
  if (state === 'connected' || state === 'active') return 'active';
  return 'connecting';
}

function reconcileNativeCallState(options: {
  navigate?: (to: string) => void;
  preferMinimized?: boolean;
  allowNavigate?: boolean;
} = {}): boolean {
  if (!isWebViewMode) return false;

  const nativeCall = getActiveNativeCalls()
    .filter((call) => call.state !== 'ended')
    .find((call) => call.runtimeMode === 'video-native')
    || null;
  if (!nativeCall) return false;

  const store = useCallStore.getState();
  const existing = store.activeCall;

  if (existing) {
    if (existing.callSessionId !== nativeCall.callSessionId) return false;
    store.setActiveCall({
      ...existing,
      status: mapNativeCallStatus(nativeCall.state),
      startedAt: nativeCall.connectedAtMs > 0
        ? existing.startedAt || new Date(nativeCall.connectedAtMs)
        : existing.startedAt,
      audioEnabled: !nativeCall.muted,
      nativeState: nativeCall.state,
      nativeRuntimeMode: nativeCall.runtimeMode,
      localVideoEnabled: nativeCall.localVideoEnabled,
      remoteVideoAvailable: nativeCall.remoteVideoAvailable
    });
    return true;
  }

  store.setActiveCall({
    callSessionId: nativeCall.callSessionId as any,
    remoteIdentityId: nativeCall.remoteIdentityId as any,
    remoteDisplayName: nativeCall.remoteDisplayName || undefined,
    localIdentityId: nativeCall.localIdentityId || undefined,
    status: mapNativeCallStatus(nativeCall.state),
    isInitiator: false,
    startedAt: nativeCall.connectedAtMs > 0 ? new Date(nativeCall.connectedAtMs) : undefined,
    videoEnabled: nativeCall.localVideoEnabled,
    audioEnabled: !nativeCall.muted,
    nativeState: nativeCall.state,
    nativeRuntimeMode: nativeCall.runtimeMode,
    localVideoEnabled: nativeCall.localVideoEnabled,
    remoteVideoAvailable: nativeCall.remoteVideoAvailable
  });

  const minimizedLaunch = consumeNativeCallMinimizedLaunchPending();
  const isCallRoute = typeof window !== 'undefined'
    && (window.location.pathname === '/call' || window.location.pathname.startsWith('/call/'));
  if (options.preferMinimized || minimizedLaunch || !options.allowNavigate || isCallRoute) {
    if (!isCallRoute) {
      useCallStore.getState().minimizeCall();
    }
  } else {
    options.navigate?.('/call');
  }

  return true;
}

/**
 * Sets up WebSocket listeners and WebRTC callbacks.
 * Call this once at app startup (e.g. in App).
 */
export function useCallListeners() {
  const navigate = useNavigate();

  useEffect(() => {
    const baseHandleCallEnd = createCallEndHandler({
      navigate,
      clearPendingCallAction,
      stopRingtone,
      stopOutgoingDialTone,
      endCallState: useCallStore.getState().endCall,
      setLastCallEndInfo: useCallStore.getState().setLastCallEndInfo,
      markCallEndedInHistory,
      getActiveCallDurationSeconds,
      consumePendingPostCallJoin
    });

    const incomingCallStaleTimerRef: { current: () => void } = {
      current: () => {}
    };

    const handleCallEnd = (info?: { reason?: string; source?: 'ws' | 'webrtc' | 'user' | 'security' | 'unknown' }) => {
      incomingCallStaleTimerRef.current();

      const state = useCallStore.getState();
      const sessionId = state.activeCall?.callSessionId || state.incomingCall?.callSessionId || null;
      if (sessionId) {
        closeWindowIncomingCallNotification(sessionId);
        postToServiceWorker({ type: 'CLOSE_CALL_NOTIFICATION', callSessionId: sessionId });
      }

      baseHandleCallEnd(info);
    };

    const incomingCallUiEffects = createIncomingCallUiEffects({
      closeWindowIncomingCallNotification,
      postToServiceWorker,
      playRingtone,
      stopRingtone,
      clearIncomingCallStaleTimer: () => incomingCallStaleTimerRef.current(),
      markCallEndedInHistory,
      handleCallEnd
    });

    const peerHandlers = createCallPeerHandlers({
      handleCallEnd
    });
    const incomingCoordination = createIncomingCallCoordination({
      incomingCallUiEffects,
      stopRingtone,
      postToServiceWorker,
      handleCallEnd,
      incomingCallStaleTimeoutMs: INCOMING_CALL_STALE_TIMEOUT_MS
    });
    incomingCallStaleTimerRef.current = incomingCoordination.clearIncomingCallStaleTimer;

    const {
      handleIncomingCall,
      handleCallAnswered,
      handleRenegotiateOffer,
      handleRenegotiateAnswer,
      handleIceCandidate,
      handleCallEnded,
      handleWebSocketError
    } = createCallWsHandlers({
      handleCallEnd,
      scheduleIncomingCallStaleTimeout: incomingCoordination.scheduleIncomingCallStaleTimeout,
      scheduleAutoIceRecovery: peerHandlers.scheduleAutoIceRecovery,
      incomingCallUiEffects,
      readPendingCallAction,
      clearPendingCallAction,
      stopRingtone,
      stopOutgoingDialTone,
      shouldIgnoreCallEndedSession
    });

    peerHandlers.install();

    const unsubscribeIncoming = wsClient.on('call:incoming', handleIncomingCall);
    const unsubscribeAnswered = wsClient.on('call:answered', handleCallAnswered);
    const unsubscribeIceCandidate = wsClient.on('call:ice-candidate', handleIceCandidate);
    const unsubscribeEnded = wsClient.on('call:ended', handleCallEnded);
    const unsubscribeWsError = wsClient.on('error', handleWebSocketError);
    const unsubscribeRenegotiateOffer = wsClient.on('call:renegotiate-offer', handleRenegotiateOffer);
    const unsubscribeRenegotiateAnswer = wsClient.on('call:renegotiate-answer', handleRenegotiateAnswer);

    return () => {
      unsubscribeIncoming();
      unsubscribeAnswered();
      unsubscribeIceCandidate();
      unsubscribeEnded();
      unsubscribeWsError();
      unsubscribeRenegotiateOffer();
      unsubscribeRenegotiateAnswer();
      incomingCoordination.cleanup();
      peerHandlers.cleanup();
    };
  }, [navigate]);

  useEffect(() => {
    let heartbeatTimer: number | null = null;
    let heartbeatKey = '';

    const stopHeartbeat = () => {
      heartbeatKey = '';
      if (heartbeatTimer !== null) {
        window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const maybeStartHeartbeat = () => {
      const activeCall = useCallStore.getState().activeCall;
      const callSessionId = String(activeCall?.callSessionId || '').trim();
      const connectedAt = activeCall?.startedAt?.getTime?.() || 0;
      const nextKey = callSessionId && connectedAt > 0 ? `${callSessionId}:${connectedAt}` : '';

      if (!nextKey) {
        stopHeartbeat();
        return;
      }

      if (heartbeatKey === nextKey && heartbeatTimer !== null) {
        return;
      }

      stopHeartbeat();
      heartbeatKey = nextKey;

      const sendHeartbeat = () => {
        void sendCallHeartbeat({
          callSessionId,
          connectedAt,
          sentAt: Date.now()
        }).catch(() => {});
      };

      sendHeartbeat();
      heartbeatTimer = window.setInterval(sendHeartbeat, CALL_HISTORY_HEARTBEAT_INTERVAL_MS);
    };

    maybeStartHeartbeat();
    const unsubscribe = useCallStore.subscribe(() => {
      maybeStartHeartbeat();
    });

    return () => {
      unsubscribe();
      stopHeartbeat();
    };
  }, []);

  useEffect(() => {
    const stopDialToneUnlessRinging = () => {
      const activeCall = useCallStore.getState().activeCall;
      if (activeCall?.status === 'ringing' && activeCall.connectionPhase === 'ringing') return;
      stopOutgoingDialTone();
    };

    stopDialToneUnlessRinging();
    const unsubscribe = useCallStore.subscribe(stopDialToneUnlessRinging);

    return () => {
      unsubscribe();
      stopOutgoingDialTone();
    };
  }, []);

  useEffect(() => {
    if (!isWebViewMode) return;

    const reconcile = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      reconcileNativeCallState({ preferMinimized: true });
    };

    reconcile();
    const interval = window.setInterval(reconcile, NATIVE_CALL_RECONCILE_INTERVAL_MS);
    const reconcileSoon = () => window.setTimeout(reconcile, 0);

    window.addEventListener('focus', reconcileSoon);
    window.addEventListener('pageshow', reconcileSoon);
    document.addEventListener('visibilitychange', reconcileSoon);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', reconcileSoon);
      window.removeEventListener('pageshow', reconcileSoon);
      document.removeEventListener('visibilitychange', reconcileSoon);
    };
  }, []);
}

/**
 * Call actions hook (does not install listeners).
 */
export function useCall() {
  const navigate = useNavigate();
  const { endCall, dispatchCallState } = useCallStore();
  const contacts = useVaultStore((state) => Object.values(state.vaultData?.contacts || {}));

  const ensureNoOngoingCall = (): boolean => {
    const state = useCallStore.getState();
    if (isWebViewMode && (state.activeCall || state.incomingCall)) {
      const nativeCalls = getActiveNativeCalls().filter((call) => call.state !== 'ended');
      if (nativeCalls.length === 0) {
        endCall();
        return true;
      }
    }
    if (state.activeCall || state.incomingCall) {
      showToast({
        message: t('call.errors.callAlreadyActive'),
        variant: 'warning',
        durationMs: 5000
      });
      return false;
    }
    return true;
  };

  const orchestrator = createCallOrchestrator({
    contacts,
    navigate,
    endCallState: endCall,
    dispatchCallState,
    tryUnlockCallSounds,
    ensureNoOngoingCall,
    clearPendingCallAction,
    stopRingtone,
    stopOutgoingDialTone,
    playOutgoingDialTone,
    playRingtone,
    isLocalIncomingCallLeader,
    markCallEndedInHistory,
    getActiveCallDurationSeconds,
    preflightWebVideoMediaAccess,
    consumePendingPostCallJoin
  });

  const pendingActionHandlers = createPendingActionHandlers({
    answerCall: orchestrator.answerCall,
    rejectCall: orchestrator.rejectCall
  });

  useEffect(() => {
    const unsub = subscribeToCallNativeEvents({
      navigate,
      handleCallEnd: orchestrator.handleCallEnd
    });

    // Recovery: if Activity was recreated while a native call was running,
    // the web app starts fresh with no activeCall. Restore it from the bridge.
    reconcileNativeCallState({ navigate, allowNavigate: true });

    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    return pendingActionHandlers.bind();
  }, [pendingActionHandlers]);

  return {
    initiateCall: orchestrator.initiateCall,
    initiateCallToIdentity: orchestrator.initiateCallToIdentity,
    answerCall: orchestrator.answerCall,
    rejectCall: orchestrator.rejectCall,
    endCall: orchestrator.endActiveCall,
    toggleVideo,
    toggleAudio
  };
}

export { tryUnlockCallSounds, tryUnlockRingtoneAudio } from '../call/callSoundEffects';
